import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const ViewerContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  color: #8a2be2;
  text-align: center;
  margin-bottom: 2rem;
  font-size: 2rem;
`;

const Content = styled.div`
  line-height: 1.6;
  font-size: 1rem;
  
  h1, h2, h3, h4, h5, h6 {
    color: #4a5d4e;
    margin-top: 2rem;
    margin-bottom: 1rem;
  }
  
  p {
    margin-bottom: 1rem;
  }
  
  blockquote {
    border-left: 4px solid #cc99ff;
    padding-left: 1rem;
    margin: 1rem 0;
    font-style: italic;
  }
  
  a {
    color: #0066cc;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: #666;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: #d32f2f;
  background: #ffebee;
  border-radius: 6px;
`;

const BackButton = styled.button`
  background: #8a2be2;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  cursor: pointer;
  margin-bottom: 1rem;
  
  &:hover {
    background: #6a1b9a;
  }
`;

const HtmlContentViewer = ({ filePath, title }) => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Función para limpiar caracteres especiales
  const cleanSpecialChars = (text) => {
    return text
      .replace(/&aacute;/g, 'á')
      .replace(/&eacute;/g, 'é')
      .replace(/&iacute;/g, 'í')
      .replace(/&oacute;/g, 'ó')
      .replace(/&uacute;/g, 'ú')
      .replace(/&ntilde;/g, 'ñ')
      .replace(/&ccedil;/g, 'ç')
      .replace(/&ordf;/g, 'ª')
      .replace(/&ordm;/g, 'º')
      .replace(/&iquest;/g, '¿')
      .replace(/&iexcl;/g, '¡')
      .replace(/&uuml;/g, 'ü')
      .replace(/&ouml;/g, 'ö')
      .replace(/&auml;/g, 'ä')
      .replace(/&egrave;/g, 'è')
      .replace(/&agrave;/g, 'à')
      .replace(/&ocirc;/g, 'ô')
      .replace(/&acirc;/g, 'â')
      .replace(/&ecirc;/g, 'ê')
      .replace(/&icirc;/g, 'î')
      .replace(/&ucirc;/g, 'û')
      .replace(/&atilde;/g, 'ã')
      .replace(/&otilde;/g, 'õ')
      .replace(/&etilde;/g, 'ẽ')
      .replace(/&itilde;/g, 'ĩ')
      .replace(/&utilde;/g, 'ũ');
  };

  useEffect(() => {
    const loadContent = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch(filePath);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const html = await response.text();
        
        // Decodificar caracteres especiales
        let decodedHtml = html;
        try {
          decodedHtml = decodeURIComponent(escape(html));
        } catch (e) {
          console.log('Usando HTML original para codificación');
        }
        
        // Crear un elemento temporal para parsear el HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = decodedHtml;
        
        // Extraer el contenido principal
        const mainContent = tempDiv.querySelector('td[bgcolor="#FFFFFF"]') || 
                           tempDiv.querySelector('body') || 
                           tempDiv;
        
        if (mainContent) {
          const cleanedContent = cleanSpecialChars(mainContent.innerHTML);
          setContent(cleanedContent);
        } else {
          setError('No se pudo extraer el contenido del archivo');
        }
      } catch (error) {
        console.error('Error cargando contenido:', error);
        setError(`Error al cargar el contenido: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    if (filePath) {
      loadContent();
    }
  }, [filePath]);

  const handleBack = () => {
    window.history.back();
  };

  if (loading) {
    return (
      <ViewerContainer>
        <LoadingMessage>
          <p>Cargando contenido...</p>
        </LoadingMessage>
      </ViewerContainer>
    );
  }

  if (error) {
    return (
      <ViewerContainer>
        <BackButton onClick={handleBack}>
          ← Volver
        </BackButton>
        <ErrorMessage>
          <p>{error}</p>
        </ErrorMessage>
      </ViewerContainer>
    );
  }

  return (
    <ViewerContainer>
      <BackButton onClick={handleBack}>
        ← Volver
      </BackButton>
      
      <Title>{title}</Title>
      
      <Content 
        dangerouslySetInnerHTML={{ __html: content }} 
      />
    </ViewerContainer>
  );
};

export default HtmlContentViewer; 